import React from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "../auth/AuthContext";
import Layout from "../components/Layout";
import ScrollToTop from "../components/ScrollToTop";
import PublicSharePage from "../pages/PublicSharePage";
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import ProtectedRoute from "../components/ProtectedRoute";
import AdminRoute from "../components/AdminRoute";
import AdminLayout from "../components/AdminLayout";
import LandingV2 from "../pages/LandingV2";
import PublicDiscoveryPage from "../pages/PublicDiscoveryPage";
import PublicListingPage from "../pages/PublicListingPage";
import SearchPage from "../pages/SearchPage";
import SearchResultsPage from "../pages/SearchResultsPage";
import LoginPage from "../pages/LoginPage";
import RegisterPage from "../pages/RegisterPage";
import VerifyEmailPage from "../pages/VerifyEmailPage";
import VerifyPendingPage from "../pages/VerifyPendingPage";
import VerifySuccessPage from "../pages/VerifySuccessPage";
import ForgotPasswordPage from "../pages/ForgotPasswordPage";
import ResetPasswordPage from "../pages/ResetPasswordPage";
import DeleteAccountPage from "../pages/DeleteAccountPage";
import ImpressumPage from "../pages/ImpressumPage";
import DatenschutzPage from "../pages/DatenschutzPage";
import AGBPage from "../pages/AGBPage";
import SupportPage from "../pages/SupportPage";
import DashboardPage from "../pages/DashboardPage";
import AIChatPage from "../pages/AIChatPage";
import AIMemoryPage from "../pages/AIMemoryPage";
import PreferencesPageV2 from "../pages/PreferencesPageV2";
import FavoritesPage from "../pages/FavoritesPage";
import ListingDetailPage from "../pages/ListingDetailPage";
import AdminOverviewPage from "../pages/AdminOverviewPage";
import AdminPlatformDashboardPage from "../pages/AdminPlatformDashboardPage";
import AdminImportsPage from "../pages/AdminImportsPage";
import AdminImportDetailPage from "../pages/AdminImportDetailPage";
import AdminListingsPage from "../pages/AdminListingsPage";
import AdminListingDetailPage from "../pages/AdminListingDetailPage";
import AdminFailedMappingsPage from "../pages/AdminFailedMappingsPage";
import AdminImageHealthPage from "../pages/AdminImageHealthPage";
import AdminDealflowDebugPage from "../pages/AdminDealflowDebugPage";
import AdminReferralsPage from "../pages/AdminReferralsPage";
import AdminReferralCodesPage from "../pages/AdminReferralCodesPage";
import AdminUsersPage from "../pages/AdminUsersPage";
import AdminQueryLearningPage from "../pages/AdminQueryLearningPage";
import AdminLeadsPage from "../pages/AdminLeadsPage";
import AdminOperationsPage from "../pages/AdminOperationsPage";
import AdminIapMonitorPage from "../pages/AdminIapMonitorPage";
import AdminCountryBackfillPage from "../pages/AdminCountryBackfillPage";
import AdminQualityDashboardPage from "../pages/AdminQualityDashboardPage";
import AdminSuspectQueuePage from "../pages/AdminSuspectQueuePage";
import AdminExplainabilityDetailPage from "../pages/AdminExplainabilityDetailPage";
import ShareAndEarnPage from "../pages/ShareAndEarnPage";
import TopDealsPage from "../pages/TopDealsPage";
import HeatmapPage from "../pages/HeatmapPage";
import AlertsPage from "../pages/AlertsPage";
import MapLibrePreviewPage from "../pages/MapLibrePreviewPage";
import ProfilePage from "../pages/ProfilePage";
import SeoLandingPage from "../pages/SeoLandingPage";
import NotFoundPage from "../pages/NotFoundPage";
import ConsentBanner from "../consent/ConsentBanner";
import { initMetaPixel, trackPageView } from "../lib/metaPixel";
import { getMarketingConsent, subscribe as subscribeConsent } from "../consent/consent";
import { useLocation } from "react-router-dom";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * Meta Pixel Sprint 2026-08 — page-view emitter.
 *
 * MUST be a descendant of ``<BrowserRouter>`` so ``useLocation`` sees
 * every route change. Behaviour:
 *
 *   * Without consent  → ``lastPathRef`` stays ``null``, no emission,
 *     no state pollution. The next consent-grant callback then sees
 *     "never fired yet for this path" and emits exactly one PageView.
 *   * On consent grant → init the pixel (safe to call even if
 *     already initialised) and emit the CURRENT pathname as the
 *     initial PageView, unless the route/URL is on the deny/defer
 *     list (P0.5).
 *   * On subsequent SPA route changes → emit exactly one PageView
 *     per pathname. The ref de-dupes StrictMode's second render.
 */
function MetaPixelPageViewEmitter() {
  const location = useLocation();
  const lastPathRef = React.useRef(null);
  React.useEffect(() => {
    // Initial: try to init (no-op if consent / config missing).
    initMetaPixel();
  }, []);
  React.useEffect(() => {
    // If consent changes later, initialise on grant AND emit the
    // current path — provided we haven't already emitted it. The
    // pre-consent branch below deliberately does NOT touch
    // `lastPathRef` (P0.1 fix), so the check works even on the
    // very first route the user landed on before consenting.
    const off = subscribeConsent((c) => {
      if (!c.decided || !c.categories.marketing) return;
      initMetaPixel();
      if (lastPathRef.current !== location.pathname) {
        lastPathRef.current = location.pathname;
        trackPageView();
      }
    });
    return () => off && off();
  }, [location.pathname]);
  React.useEffect(() => {
    // Route-change: fire once per unique pathname WHEN consented.
    // Without consent we deliberately do NOT stamp `lastPathRef`
    // (P0.1) so a later consent grant can still emit the current
    // path exactly once.
    if (!getMarketingConsent()) return;
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    trackPageView();
  }, [location.pathname]);
  return null;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <MetaPixelPageViewEmitter />
      <ConsentBanner />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Layout>
            <RouteErrorBoundary>
            <Routes>
              <Route path="/" element={<PublicDiscoveryPage />} />
              <Route path="/search/:vertical" element={<SearchPage />} />
              <Route path="/search/:vertical/results" element={<SearchResultsPage />} />
              {/* LandingV2 is the single, canonical `/` landing. The legacy
                  LandingPage and its hand-rolled locale system were removed
                  (2026-07) — the project now has exactly one landing and one
                  i18n system (react-i18next). `/landing-v2` keeps 301'ing to
                  `/` for any external bookmarks / shares. */}
              <Route
                path="/landing-v2"
                element={<Navigate to="/" replace />}
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* Phase 1 — public marketing page moved to /welcome */}
              <Route path="/welcome" element={<LandingV2 />} />
              {/* Phase 1 — anonymous listing teaser */}
              <Route path="/deal/:ulid" element={<PublicListingPage />} />
              {/* Pre-EAS — public share landing (no auth). */}
              <Route path="/d/:shareId" element={<PublicSharePage />} />
              {/* Phase H2 — Email verification + legal pages (public) */}
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/verify-pending" element={<VerifyPendingPage />} />
              <Route path="/verify-success" element={<VerifySuccessPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* Public account-deletion route — Google Play Account Deletion URL.
                  Logged out → sign-in prompt; logged in → existing delete flow. */}
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route path="/impressum" element={<ImpressumPage />} />
              <Route path="/datenschutz" element={<DatenschutzPage />} />
              <Route path="/agb" element={<AGBPage />} />
              {/* Phase 2 Block C — public support / contact form */}
              <Route path="/support" element={<SupportPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              {/* Batch 5b-1 P0-B Sub-Step 3 — canonical Preferences page.
                  /preferences mounts the cascading-dropdown V2 directly.
                  /preferences-v2 kept as an alias for any deep-links
                  that pointed to the V2 explicitly during the rollout.
                  The legacy /preferences-v1 page (free-text inputs) has
                  been deleted. */}
              <Route
                path="/preferences"
                element={
                  <ProtectedRoute>
                    <PreferencesPageV2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/preferences-v2"
                element={
                  <ProtectedRoute>
                    <PreferencesPageV2 />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/favorites"
                element={
                  <ProtectedRoute>
                    <FavoritesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/listing/:ulid"
                element={
                  <ProtectedRoute>
                    <ListingDetailPage />
                  </ProtectedRoute>
                }
              />
              {/* Phase F — Share & Earn (auth required, NO paywall).
                  Free / expired-trial users must reach this page; it's
                  how they recover Pro-access via inviting friends. */}
              <Route
                path="/share-and-earn"
                element={
                  <ProtectedRoute allowExpired>
                    <ShareAndEarnPage />
                  </ProtectedRoute>
                }
              />
              {/* TestFlight-polish round — Top Deals + Profile become
                  dedicated routes so the 5-tab mobile bottom bar (in
                  parity with native iOS) can deep-link to them. */}
              <Route
                path="/top-deals"
                element={
                  <ProtectedRoute requirePro proContext="top_deals">
                    <TopDealsPage />
                  </ProtectedRoute>
                }
              />
              {/* SHIP Cycle — dedicated Heatmap page (Leaflet). Authed
                  only; country-scalable + layer-scalable via the
                  configs under `src/config/heatmap-*`. */}
              <Route
                path="/heatmap"
                element={
                  <ProtectedRoute>
                    <HeatmapPage />
                  </ProtectedRoute>
                }
              />
              {/* V4 — AI Market Intelligence: general market chat
                  (Modus B). Listing-bound advisor stays on the
                  detail page (Modus A). */}
              <Route
                path="/ai"
                element={
                  <ProtectedRoute requirePro proContext="ai">
                    <AIChatPage />
                  </ProtectedRoute>
                }
              />
              {/* V4.12 Memory Center — "Was weiß die AI über mich?" */}
              <Route
                path="/ai/memory"
                element={
                  <ProtectedRoute requirePro proContext="ai">
                    <AIMemoryPage />
                  </ProtectedRoute>
                }
              />
              {/* V4.12 Phase E — Saved Searches / Alerts */}
              <Route
                path="/alerts"
                element={
                  <ProtectedRoute>
                    <AlertsPage />
                  </ProtectedRoute>
                }
              />
              {/* MapTiler infra prep — MapLibre verification surface,
                  not linked in any nav. Prod maps stay on Leaflet. */}
              <Route
                path="/map-preview"
                element={
                  <ProtectedRoute>
                    <MapLibrePreviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute allowExpired>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              {/* M7b — admin sub-routes (all admin-only via AdminRoute wrapping AdminLayout) */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<AdminPlatformDashboardPage />} />
                <Route path="system" element={<AdminOverviewPage />} />
                <Route path="imports" element={<AdminImportsPage />} />
                <Route path="imports/:ulid" element={<AdminImportDetailPage />} />
                <Route path="listings" element={<AdminListingsPage />} />
                <Route path="listings/:ulid" element={<AdminListingDetailPage />} />
                <Route path="failed-mappings" element={<AdminFailedMappingsPage />} />
                <Route path="image-health" element={<AdminImageHealthPage />} />
                <Route path="dealflow-debug" element={<AdminDealflowDebugPage />} />
                {/* Phase F */}
                <Route path="referrals" element={<AdminReferralsPage />} />
                {/* Phase V1.5 — admin-managed marketing codes */}
                <Route path="referral-codes" element={<AdminReferralCodesPage />} />
                {/* Phase 3A — manual user management (review accounts, Pro grants) */}
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="query-learning" element={<AdminQueryLearningPage />} />
                <Route path="leads" element={<AdminLeadsPage />} />
                {/* Sprint Q O.3 — System Controls (feature flags + market recompute + scheduler) */}
                <Route path="operations" element={<AdminOperationsPage />} />
                {/* P0 Hotfix 2026-07-08 — Apple IAP monitor + rescue console */}
                <Route path="iap-monitor" element={<AdminIapMonitorPage />} />
                {/* Phase 2a — Country-First backfill control panel */}
                <Route path="country-backfill" element={<AdminCountryBackfillPage />} />
                {/* Batch 4 Section F — Quality Dashboard */}
                <Route path="quality" element={<AdminQualityDashboardPage />} />
                {/* V4.9 Phase 7 — Suspicious Listings Quarantine Queue */}
                <Route path="suspect-queue" element={<AdminSuspectQueuePage />} />
                {/* Batch 5b-1 Section 1 — Explainability detail per listing */}
                <Route path="explainability/:ulid" element={<AdminExplainabilityDetailPage />} />
              </Route>
              {/* SEO-Sprint 06.10.2026 — öffentliche Einstiegsseiten je
                  Suchabsicht: `/{sprache}/{slug}`. React Router bewertet
                  statische Segmente höher, deshalb gewinnen alle oben
                  deklarierten Routen; unbekannte Zwei-Segment-Pfade
                  landen über `findSeoPage() === null` auf der 404-Seite.
                  Definitionen: src/seo/landing-pages.json. */}
              <Route path="/:lang/:slug" element={<SeoLandingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </RouteErrorBoundary>
          </Layout>
          <Toaster theme="dark" position="bottom-right" richColors />
        </AuthProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
